import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFeather, FiUsers, FiCompass, FiHeart, FiLayers, FiStar } from 'react-icons/fi';

const FEATURES = [
  {
    icon: FiFeather,
    title: 'Daily Guided Journaling',
    description: 'A personalized prompt every morning, tailored to your growth archetype. You never face a blank page — just a question that cuts straight to what matters today.',
    img: '/web_images/Andlegur%20web%205.webp',
    imgAlt: 'Daily guided journaling on Andlegur',
    card: 'bg-[#0E1833] border border-indigo-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-indigo-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-indigo-300',
  },
  {
    icon: FiStar,
    title: 'Personal Archetype System',
    description: 'Six archetypes. One that fits you with uncomfortable precision. Discover your growth pattern, your emotional tendencies, and how you actually change — not how you think you do.',
    img: '/web_images/Andlegur%20web%207.webp',
    imgAlt: 'Personal archetype system on Andlegur',
    card: 'bg-[#0E1833] border border-violet-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-violet-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-violet-300',
  },
  {
    icon: FiUsers,
    title: 'Community Circles',
    description: 'A rare thing online: honest conversation. Topic-based circles built for depth — not performance. Moderated, purposeful, and full of people who actually mean it.',
    img: '/web_images/Andlegur%20web%209.webp',
    imgAlt: 'Community circles on Andlegur',
    card: 'bg-[#0E1833] border border-sky-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-sky-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-sky-300',
  },
  {
    icon: FiCompass,
    title: 'Guided Practices',
    description: 'Five to fifteen minutes that change the texture of your entire day. Structured exercises for emotional resilience, clarity, and values alignment — designed to fit real life.',
    img: '/web_images/Andlegur%20web%2014.webp',
    imgAlt: 'Guided practices on Andlegur',
    card: 'bg-[#0E1833] border border-teal-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-teal-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-teal-300',
  },
  {
    icon: FiHeart,
    title: 'AI Archetype Companions',
    description: 'Six AI companions, each built around a different growth archetype. Context-aware conversation for moments when you need to process something and no one nearby can hold that.',
    img: '/web_images/Andlegur%20web%2021.webp',
    imgAlt: 'AI archetype companions on Andlegur',
    card: 'bg-[#0E1833] border border-rose-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-rose-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-rose-300',
  },
  {
    icon: FiLayers,
    title: 'Cross-Platform Sync',
    description: 'iOS, Android, web. Your journal, your community, your growth data — everywhere you are. The sanctuary moves with you.',
    img: '/web_images/Andlegur%20web%2024.webp',
    imgAlt: 'Cross-platform sync on Andlegur iOS Android web',
    card: 'bg-[#0E1833] border border-amber-400/40 rounded-2xl overflow-hidden flex flex-col group',
    box: 'w-10 h-10 rounded-xl border border-amber-400/40 bg-white/5 flex items-center justify-center',
    ico: 'w-5 h-5 text-amber-300',
  },
];

const FeaturesPage = () => (
  <>
    <Helmet>
      <title>Andlegur Features | Journaling, Reflection, Archetypes and Community</title>
      <meta name="description" content="Explore every Andlegur feature — guided daily journaling, personal archetype insights, community circles, reflection prompts, and guided practices. Free on iOS, Android and web." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://andlegur.com/features" />
    </Helmet>

    <div className="bg-[#05091B] text-indigo-100 overflow-x-hidden">

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%201.webp" alt="" className="w-full h-full object-cover opacity-30 mix-blend-screen" loading="eager" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/50 to-[#05091B]/60" />
          <div className="absolute inset-0 bg-linear-to-r from-[#05091B] via-transparent to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-indigo-400 text-xs uppercase tracking-widest mb-5">
            Platform Overview
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Most apps distract you from your own life.<br />
            <span className="text-indigo-300">Andlegur brings you back to it.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-indigo-200 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            You are not looking for another app. You are looking for a reason to return to yourself. Here is everything Andlegur gives you to do that — every single day.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Get Started Free →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Download the App</Link>
          </motion.div>
        </div>
      </section>

      {/* Split section */}
      <section className="py-24 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">Why It Works</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-5 leading-snug">
                You have been meaning to do this work for years.<br />
                <span className="text-indigo-300">The problem was never motivation. It was structure.</span>
              </h2>
              <div className="w-12 h-px bg-indigo-500 mb-7" />
              <p className="text-indigo-200 text-base leading-relaxed mb-5">
                Generic wellness apps give you generic tools. Andlegur starts with who you are — your archetype, your patterns, your actual growth edge — and builds an entire personalized experience around that.
              </p>
              <p className="text-indigo-300 text-base leading-relaxed">
                Journaling that knows what to ask. Community that actually has something to say. Practices that fit where you are right now. This is what it looks like when every tool is built for one purpose: helping you become who you already know you can be.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: '480px' }}>
              <img src="/web_images/Andlegur%20web%2020.webp" alt="Andlegur complete platform for personal growth" className="w-full h-full object-cover absolute inset-0" loading="lazy" />
              <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
              <p className="absolute bottom-8 left-8 right-8 text-white text-xl font-light italic leading-snug">
                "The version of you that is 3 months ahead — starts today."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="py-20 border-t border-indigo-800/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">Everything Included</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">
              Six features.<br /><span className="text-indigo-300">One direction: inward.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, img, imgAlt, card, box, ico, title, description }, i) => (
              <motion.article key={title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }} className={card}>
                <div className="p-7 flex flex-col gap-4 flex-1">
                  <div className={box}><Icon className={ico} /></div>
                  <h3 className="text-white font-semibold text-lg">{title}</h3>
                  <p className="text-indigo-300 text-sm leading-relaxed">{description}</p>
                </div>
                <div className="relative h-48 overflow-hidden shrink-0">
                  <img src={img} alt={imgAlt} className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-linear-to-t from-[#0E1833]/60 via-transparent to-transparent" />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="relative py-28 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2023.webp" alt="" className="w-full h-full object-cover opacity-15 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/70 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-indigo-400 text-xs uppercase tracking-widest mb-6">What Members Say</p>
          <blockquote className="text-white text-2xl sm:text-3xl font-light italic leading-relaxed mb-8">
            "I have tried every journaling app. Andlegur is the only one that made me feel like it actually understood what I needed to work on."
          </blockquote>
          <p className="text-indigo-400 text-sm">— Andlegur member, The Reflector archetype</p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden border-t border-indigo-800/40">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src="/web_images/Andlegur%20web%2029.webp" alt="" className="w-full h-full object-cover opacity-20 mix-blend-screen" loading="lazy" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-[#05091B]/60 to-[#05091B]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-tight">
            Free to start.<br /><span className="text-indigo-300">Impossible to ignore.</span>
          </h2>
          <p className="text-indigo-300 text-lg leading-relaxed mb-10">
            Create your account, discover your archetype, and receive your first personalized reflection prompt in under 2 minutes. No credit card. No friction. Just you, finally showing up for yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join" className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl">Create Free Account →</Link>
            <Link to="/download" className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300">Download the App</Link>
          </div>
        </div>
      </section>
    </div>
  </>
);

export default FeaturesPage;
